import React, { memo } from 'react';
import { Tooltip } from '@mui/material';
import type { DoctorShift } from '../../../../../types/doctorShift';
import { ShiftBarNormal, ShiftBarUrgent } from '../../MonthlyDoctorsShift.styles';
import type { TimelinePosition } from '../../hooks/useTimelinePosition';

/**
 * ShiftBar Props
 */
interface ShiftBarProps {
  /** シフトデータ */
  shift: DoctorShift;
  /** 位置情報 */
  position: TimelinePosition;
  /** クリックハンドラー */
  onClick: (shift: DoctorShift) => void;
  /** 読み取り専用モード */
  readOnly?: boolean;
}

/**
 * シフトバーコンポーネント
 *
 * @description
 * 医師シフトをカラーバーで表示する。
 * クリックで編集ポップアップを開く。ツールチップで詳細情報を表示。
 *
 * @param {ShiftBarProps} props - コンポーネントProps
 * @returns {React.ReactElement} シフトバー
 *
 * @example
 * <ShiftBar
 *   shift={shift}
 *   position={{ left: 10, width: 20 }}
 *   onClick={handleShiftClick}
 * />
 */
const ShiftBar: React.FC<ShiftBarProps> = memo(({ shift, position, onClick, readOnly = false }) => {
  const isUrgent = shift.slot_type === 'urgent';
  const BarComponent = isUrgent ? ShiftBarUrgent : ShiftBarNormal;

  /**
   * クリックハンドラー
   */
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!readOnly) {
      onClick(shift);
    }
  };

  /**
   * ツールチップの内容
   */
  const tooltipContent = (
    <>
      <div>{shift.doctor_name || shift.doctor_full_name || ''}</div>
      <div>
        {shift.start_time} - {shift.end_time}
      </div>
      {isUrgent && <div style={{ color: '#ff6b6b' }}>急募枠</div>}
      {readOnly && <div style={{ color: '#999' }}>（閲覧のみ）</div>}
    </>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="top">
      <BarComponent
        onClick={handleClick}
        style={{
          left: `${position.left}%`,
          width: `${position.width}%`,
          cursor: readOnly ? 'default' : 'pointer',
        }}
      >
        {shift.doctor_name || shift.doctor_full_name || ''}先生 {shift.start_time}~{shift.end_time}
      </BarComponent>
    </Tooltip>
  );
});

ShiftBar.displayName = 'ShiftBar';

export { ShiftBar };
